/**
 * All the properties of an activity
 */
export interface Activity {
	questId: number;
	name: string;
	activityType: ActivityType;
	realm: Realm;
}

/**
 * All the properties to add an activity
 */
export interface ActivityAdd {
	questId: number;
	activityType: ActivityType;
}

/**
 * List of activities to operate on
 */
export type ActivityAddList = Array<ActivityAdd>;

/**
 * List of activities
 */
export type ActivityList = Array<Activity>;

/**
 * Activity Progression
 */
export interface ActivityProgress {
	questName: string;
	questId: number;
	activityType: string;
	status: boolean;
}

/**
 * All the properties of an activity
 */
export const AllActivityTypes = [
	"quiz",
	"game",
	"book",
	"animation",
	"song",
] as const;

/**
 * A type of activity
 */
export type ActivityType = typeof AllActivityTypes[number];

/**
 * All the properties of an assignment which would be returned from the server.
 */
export interface Assignment extends AssignmentForEditing {
	assignmentId: string;
	status: string;
	activities: ActivityList;
}

/**
 * All the properties of to create an Assignment.
 */
export interface AssignmentAdd extends AssignmentForEditing {
	activities: ActivityAddList;
}

/**
 * All the properties of an assignment which can be edited
 */
export interface AssignmentForEditing {
	name: string;
	groupId: string;
	startDate: string;
	endDate: string;
}

/**
 * All the properties of an assignment with user progress
 */
export interface AssignmentProgress {
	name: string;
	assignmentId: string;
	groupId: string;
	startDate: string;
	endDate: string;
	progress: Array<ActivityProgress>;
}

export interface AuthSigninBody {
	email?: string;
	password?: string;
}

/**
 * All the user properties necessary to create a user.
 */
export interface CreateUser extends Required<UserOptionalPropsForEditing> {
	role: Roles;
}

/**
 * Payload shape for the register-users endpoint. Extends CreateUser with a
 * manually supplied initial password. Kept separate so CreateUser/User are not
 * widened with a password field across the rest of the app.
 */
export interface CreateUserRequest extends CreateUser {
	password: string;
}

/**
 * All the properties of a group which would be returned from the server.
 */
export interface Group extends GroupWithoutUsers {
	students: UserListBasic;
}

/**
 * All the properties of a group which can be edited (apart from the users)
 */
export interface GroupForEditing {
	/**
	 * The name of the group
	 */
	name?: string;
	owners?: Array<string>;
}

/**
 * All of the properties of a group, **without the list of users**.
 */
export interface GroupWithoutUsers {
	groupId: string;
	/**
	 * The name of the group
	 */
	name: string;
	owners: UserListBasic;
}

export const AllRealmTypes = [
	"Green Acres",
	"Habiterra",
	"Edison's Isle",
	"Cape Cosmos",
	"DNAtion",
	"Elixir Institute",
	"E=MC Square",
	"Fahren Heights",
	"Prehistoric Point",
	"Pythagoras Peninsula",
] as const;

/**
 * A realm
 */
export type Realm = typeof AllRealmTypes[number];

/**
 * Contains all the properties needed to create a new school with a new admin or teacher signup.
 */
export interface RegisterAdmin extends CreateUser {
	license?: SchoolLicense;
	password: string;
	schoolName: string;
}

/**
 * All the properties of a group. The creator of this group will automatically become an owner of the group.
 */
export interface RegisterGroup {
	/**
	 * The name of the group
	 */
	name: string;
	/**
	 * The UUIDs of the students who will be registered with this group
	 */
	students: Array<string>;
}

/**
 * Response containing newly created user credentials.
 */
export interface RegisterUsersResponse {
	/**
	 * Array containing the email addresses of the users that were already present in the database.
	 */
	"already-present"?: Array<string>;
	/**
	 * Array containing the newly created user credentials.
	 */
	new_users?: Array<UserOptionalPropsAndPasswordTemp>;
}

export const AllRoleTypes = [
	"admin",
	"admin-teacher",
	"teacher",
	"student",
	"parent",
] as const;
export type RoleType = typeof AllRoleTypes[number];

/**
 * Array containing the possible roles. This should only contain a single role.
 */
export type Roles = Array<RoleType>;

export interface School {
	schoolId: string;
	schoolName: string;
	license: SchoolLicense;
	admins: Array<User>;
}

export const AllSchoolLicenses = ["teacher", "school", "parent"] as const;

export type SchoolLicense = typeof AllSchoolLicenses[number];

export const AllSortOrders = ["asc", "desc"] as const;

export type SortOrder = typeof AllSortOrders[number];

/**
 * UUID to represent an item. Operations will use this instead of the item's primary key. UUIDs are used to prevent programmatic scraping of data - see https://www.wired.com/story/parler-hack-data-public-posts-images-video/ for an example of why not to order items by number.
 */
export type UUID = string;

/**
 * All of the properties of a user which are commonly returned by APIs to the clients.
 */
export interface User extends CreateUser {
	userId: string;
	groups?: Array<GroupWithoutUsers>;
	yearLevel?: string;
}

/**
 * Main user attributes to be returned
 */
export interface UserBasic {
	userId: string;
	firstName: string;
	lastName: string;
	email: string;
}

/**
 * More detailed return for group user
 */
export interface UserGroupReturn extends Required<UserBasic> {
	isOwner: boolean;
	roles: Roles;
}

/**
 * group basic users with just roles
 */
export interface RegisteredUser extends Required<UserBasic> {
	role: Roles;
	yearLevel: string;
}

export type RegisteredUserList = Array<RegisteredUser>;

/**
 * List of user UUIDs to operations on
 */
export type UserList = Array<User>;

/**
 * List of main user attributes to be returned
 */
export type UserListBasic = Array<UserBasic>;

/**
 * All the optional user properties, in addition to the password.
 */
export interface UserOptionalPropsAndPasswordForEditing
	extends UserOptionalPropsForEditing {
	password?: string;
}

/**
 * Response containing newly created user credentials.
 */
export interface UserOptionalPropsAndPasswordTemp
	extends UserOptionalPropsAndPasswordForEditing {
	role?: Roles;
}

/**
 * All the user properties, apart from the userId, role and password. All the properties are optional.
 */
export interface UserOptionalPropsForEditing {
	firstName?: string;
	lastName?: string;
	email?: string;
}

/**
 * Details for a assignment report of a user
 */
export interface UserReport extends UserBasic {
	progress: Array<boolean>;
}

export interface UserResponseWithActivities extends User {
	activities?: Array<AssignmentProgress>;
}

/**
 * All the common user details
 */
export interface UserWithProgress extends CreateUser {
	userId: string;
	assignments?: Array<AssignmentProgress>;
}

/**
 * Type guard.
 */
export const isUserWithProgress = (user: User): user is UserWithProgress => {
	return !!(user as UserWithProgress).assignments;
};

export interface IndividualResult {
	userId: string;
	firstName: string;
	lastName: string;
	yearLevel: number;
	date: string;
	totalAssignment: number;
	totalCorrect: number;
	totalAnswered: number;
	assignmentResultList: Array<IndividualAssignmentResult>;
	curriculumResultList: Array<CurriculumResult>;
	capabilityResultList: Array<CapabilityResult>;
	capabilityGradeList: Array<Grade>;
	topicGradeList: Array<Grade>;
	commentList: Array<Comment>;
}

export interface GroupResult {
	groupId: string;
	name: string;
	yearLevel: number;
	date: string;
	totalAssignment: number;
	totalCorrect: number;
	totalAnswered: number;
	groupAssignmentResultList: Array<GroupAssignmentResult>;
	curriculumResultList: Array<CurriculumResult>;
	capabilityResultList: Array<CapabilityResult>;
	commentList: Array<Comment>;
}

export interface Appendix {
	yearLevel: number;
	learningAreaList: Array<LearningArea>;
}

export interface Capability {
	id: number;
	name: string;
	description: string;
}

export interface Elaboration {
	id: string;
	description: string;
	capabilityList: Array<Capability>;
}

export interface Curriculum {
	id: string;
	name: string;
	code: string;
	description: string;
	learningArea: string;
	strand: string;
	yearLevel: string;
	elaborationList?: Array<Elaboration>;
	capabilityList: Array<Capability>;
}

export interface Strand {
	name: string;
	yearLevel: string;
	description: string;
	curriculumList: Array<Curriculum>;
}

export interface LearningArea {
	name: string;
	yearLevel: string;
	description: string;
	strandList: Array<Strand>;
	achievementStandard: string;
}

export interface AssignmentResult {
	assignmentDetails: Assignment;
	correct: number;
	answered: number;
	status: "Scheduled" | "In Progress" | "Completed";
	curriculumList: Array<Curriculum>;
	description?: string;
}

export interface IndividualAssignmentResult extends AssignmentResult {
	userId: string;
	ranking?: string;
}

export interface GroupAssignmentResult extends AssignmentResult {}

export interface CurriculumResult {
	userId: string;
	curriculumDetails: Curriculum;
	evaluation: string;
}

export interface CapabilityResult {
	userId: string;
	capabilityDetails: Capability;
	evaluation: string;
}

export interface Grade {
	name: string;
	grade: string;
}

export interface GradeById {
	id: string;
	grade: string;
}

export interface GetGrade {
	gradeId: string;
	name: string;
	grade: string;
}

export interface Comment {
	commentId: string;
	commentFrom: string;
	commentTo: string;
	date: string;
	comment: string;
}

export interface ResultProps {}

export interface UserResultProps extends ResultProps {
	date: string;
	name: string;
	yearLevel: string;
	totalAssignment: string;
	totalCorrect: string;
	totalAnswered: string;
	assignmentResults: Array<IndividualAssignmentResult>;
	curriculumResults: Array<CurriculumResult>;
	capabilityGrades: Array<Grade>;
	topicGrades: Array<Grade>;
	comments: Array<Comment>;
	id?: UUID;
}

export interface MultiUserResultProps extends ResultProps {
	propsList: UserResultProps[];
}

export interface GroupResultProps extends ResultProps {
	date: string;
	name: string;
	yearLevel: string;
	totalAssignment: string;
	totalCorrect: string;
	totalAnswered: string;
	groupAssignmentResults: Array<GroupAssignmentResult>;
	curriculumResults: Array<CurriculumResult>;
	comments: Array<Comment>;
	id?: UUID;
}

export interface MultiGroupResultProps extends ResultProps {
	propsList: GroupResultProps[];
}

export interface MetricsProps extends ResultProps {
	imageURLs: Map<string, string>;
}

export const AllTopicTypes = [
	"Medicine & Health",
	"Environment & Nature",
	"Life & Evolution",
	"Dinosaurs and Fossils",
	"Physics",
	"Animals",
	"Weather",
	"Inventions",
	"Space",
	"Mathematics",
] as const;

export type Topic = typeof AllTopicTypes[number];

// Aggregated Map whose key is LearningArea
export type AreaAggMap = Map<string, Map<string, Map<string, Curriculum[]>>>;
// Aggregated Map whose key is Strand
export type StrandAggMap = Map<string, Map<string, Curriculum[]>>;
// Aggregated Map whose key is CurriculumName
export type NameAggMap = Map<string, Curriculum[]>;

/**
 * All the optional user properties, in addition to the password.
 */
export interface UserChildEmailForEditing extends UserOptionalPropsForEditing {
	childEmail?: string;
}

export interface outMetrics {
	MetricsList: Array<IndividualMetrics>;
}

export interface outSchoolMetrics {
	MetricsList: Array<SchoolMetrics>;
}

export type studentsGradeDistributionMetrics =
	Array<studentIndividualGradeCount>;

export interface studentIndividualGradeCount {
	YearLevel: number;
	StudentCount: string;
}
export interface IndividualMetrics {
	UserId: string;
	YearLevel: number;
	Year: number;
	Month: string;
	Month_num: number;
	CompleteRate: number;
	MonthlyDataList: Array<MonthlyData>;
}

export interface SchoolMetrics {
	SchoolId: string;
	Year: number;
	Month: string;
	Month_num: number;
	CompleteRate: number;
	AccuracyRate: number;
	MonthlyDataList: Array<SchoolMonthlyData>;
}

export interface SchoolMonthlyData {
	LearningArea: string;
	Topic: string;
	Answered: number;
	Total: number;
	Correct: number;
	Avgscore: number;
	Avgcomplete: number;
	Activity: number;
}

export interface MonthlyData {
	YearLevel: number;
	LearningArea: string;
	Topic: string;
	Answered: number;
	Total: number;
	Correct: number;
	Avgscore: number;
	Avgcomplete: number;
	Activity: number;
}

export const RealmsTopic: { [char: string]: string } = {
	"Green Acres": "Environment & Nature",
	Habiterra: "Animals",
	"Edison's Isle": "Inventions",
	"Cape Cosmos": "Space",
	DNAtion: "Life & Evolution",
	"Elixir Institute": "Medicine & Health",
	"E=MC Square": "Physics",
	"Fahren Heights": "Weather",
	"Prehistoric Point": "Dinosaurs and Fossils",
	"Pythagoras Peninsula": "Mathematics",
	Mathematics: "Pythagoras Peninsula",
	"Dinosaurs and Fossils": "Prehistoric Point",
	Weather: "Fahren Heights",
	Physics: "E=MC Square",
	"Medicine & Health": "Elixir Institute",
	"Life & Evolution": "DNAtion",
	Space: "Cape Cosmos",
	Inventions: "Edison's Isle",
	Animals: "Habiterra",
	"Environment & Nature": "Green Acres",
};

export const AllRealms = [
	"Elixir Institute",
	"Pythagoras Peninsula",
	"Fahren Heights",
	"Prehistoric Point",
	"Green Acres",
	"Habiterra",
	"DNAtion",
	"Cape Cosmos",
	"Edison's Isle",
	"E=MC Square",
] as const;

export const AllEvaluationTypes = [
	"Topic Learning",
	"General Capability",
	"Comment",
] as const;

export type Evaluation = typeof AllEvaluationTypes[number];

export const AllScales = [
	"Sentiment Blank",
	"Sentiment Neutral",
	"Sentiment Good",
	"Sentiment Satisfied",
	"Sentiment Very Satisfied",
] as const;

export type Scale = typeof AllScales[number];

export const AllGeneralCapabilities = [
	"Literacy",
	"Numeracy",
	"Information and Communication Technology (ICT) ",
	"Critical and Creative Thinking",
	"Personal and Social Capability",
	"Ethical Understanding",
	"Intercultural Understanding",
] as const;

export type GeneralCapability = typeof AllGeneralCapabilities[number];

export interface UpdateGrade {
	studentId: string;
	gradeType: string;
	grades: GradeById[];
}

export interface NewComment {
	studentId: string;
	staffId: string;
	comment: string;
}

// used in metrics results
export const ACTIVITY_COUNT = "1-ActivityCount";
export const ENROLLMENT_DISTRIBUTION = "2-EnrollmentDistribution";
export const COMPLETION_RATE = "3-CompletionRate";
export const ACTIVITY_ACCURACY = "4-ActivityAccuracy";
export const YEAR_LEVEL = "5-YearLevel";

export interface Children {
	firstname: string;
	lastname: string;
	id: string;
}

export interface assignmentResultCSV {
	ResultID: string;
	AssignmentID: string;
	UserID: string;
	YearLevel: string;
	Correct: string;
	Answered: string;
	Total: string;
	CompleteAt: string;
	FirstName: string;
	LastName: string;
}

export interface accuracySeries {
	name: string;
	data: any[];
	type: string;
	markPoint: {
		data: {
			type: string;
			name: string;
		}[];
	};
}

export interface SchoolResponse {
	schoolId: string;
	schoolName: string;
}

export interface GroupAccuracy {
	Name: string;
	GroupID: string;
	Complete: number;
	Accuracy: number;
	Year: number;
	Month: number;
}
